'use client';

import { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { apiFetch, API_BASE, ApiError } from '../lib/api';
import { isMustChangePassword, clearMustChangePassword } from '../lib/authStorage';
import useSWR from 'swr';
import { useAuth } from '../lib/auth';
import { useLicense } from '../lib/license';
import Card from './Card';
import Alert from './Alert';
import { useTranslation, LANGUAGES, Locale, getOrderedLanguages } from '../lib/i18n';
import { useTheme } from '../lib/theme';
import { useFontSize, type FontSize, FONT_SIZE_PX } from '../lib/fontSize';
import { updateUserCountry, useFormatLocale } from '../lib/useNationalEnvironment';
import { formatDateTimeDisplay } from '../lib/dateInput';
import { useToast } from './Toast';
import { useExitAnimation } from '../lib/useExitAnimation';
import Skeleton from './Skeleton';
import PasswordStrength from './PasswordStrength';
import ConfirmModal from './ConfirmModal';
import UserNotificationPrefs from './UserNotificationPrefs';

export type ProfileTab = 'user' | 'password' | '2fa' | 'sessions' | 'notifications' | 'account' | 'stripe' | 'google' | 'license' | 'settings';

type SyncSettings = {
  userId: number;
  autoSyncEnabled: boolean;
  syncIntervalMinutes: number;
  syncEventTypes: string;
  googleCalendarId: string;
  syncDirection: string;
  lastSyncAt: string;
};

type GoogleCalendar = { id: string; summary: string };

const GOOGLE_EVENT_TYPES = ['MEETING', 'CALL', 'IM', 'TASK', 'NOTE'];
const GOOGLE_SYNC_DIRECTIONS = ['BIDIRECTIONAL', 'FROM_GOOGLE', 'TO_GOOGLE'];

type RoleWithDescription = {
  id: number;
  name: string;
  description: string;
  priority: number;
};

/** Zobrazí role uživatele s jejich popisy (načtenými z /api/admin/roles nebo /api/roles) */
function RoleDescriptions({ roleNames }: { roleNames: string[] }) {
  const { data: allRoles } = useSWR<RoleWithDescription[]>(
    '/api/admin/roles',
    { revalidateOnFocus: false }
  );
  const { t } = useTranslation();

  if (!allRoles) {
    return <span className="text-xs text-wood">{t('loading')}</span>;
  }

  const userRoles = allRoles
    .filter(r => roleNames.includes(r.name))
    .sort((a, b) => a.priority - b.priority);

  if (userRoles.length === 0) {
    return <span className="text-xs text-wood">—</span>;
  }

  return (
    <div className="flex flex-col gap-1.5">
      {userRoles.map(r => (
        <div key={r.id} className="text-sm">
          <span className="font-medium text-bark">{r.name}</span>
          {r.description && <span className="text-wood"> — {r.description}</span>}
        </div>
      ))}
    </div>
  );
}

type ProfileContentProps = {
  /** Počáteční záložka (default 'user'). V page módu se přepíše URL hashem, pokud existuje. */
  initialTab?: ProfileTab;
  /** true (default) = číst URL hash pro výběr záložky (page mód). false = ignorovat hash (modal mód). */
  readHash?: boolean;
  /** Pokud je poskytnuto, volá se místo router.push('/login') při neautentizaci (modal mód → zavřít modal). */
  onAuthRedirect?: () => void;
  /** Pokud je poskytnuto, volá se při navigaci uvnitř obsahu (např. /?edit=true) — modal se zavře a router naviguje. */
  onNavigate?: (href: string) => void;
};

type AccountData = {
  firstName: string;
  lastName: string;
  stripeSecretKey: string;
  hasStripeSecretKey: boolean;
  stripeWebhookSecret: string;
  hasStripeWebhookSecret: boolean;
  webhookToken: string | null;
  _maskedStripeKey?: string;
  _maskedWebhookSecret?: string;
};

type BusinessAddress = {
  id: number;
  addressName: string;
  addressType: 'BILLING' | 'DELIVERY';
  companyName: string | null;
  firstName: string | null;
  lastName: string | null;
  street: string | null;
  city: string | null;
  zip: string | null;
  country: string;
  ic: string | null;
  dic: string | null;
  isDefault: boolean;
  verified: boolean;
};

type UserProfile = {
  email: string;
  username: string | null;
  role: string;
  twoFactorEnabled: boolean;
  createdAt: string;
  country?: string | null;
  idleTimeoutMinutes?: number | null;
  idleTimeoutDefault?: number;
  idleTimeoutMax?: number;
  listDisplayMode?: string | null;
};

type TwoFactorSetupResponse = {
  secret: string;
  qrCodeUrl: string;
};

type SessionInfo = {
  sessionId: string;
  userAgent: string | null;
  ipAddress: string | null;
  firstSeenAt: string | number[] | null;
  lastActiveAt: string | number[] | null;
  expiresAt: string | number[] | null;
  current: boolean;
};

/** Zkrácený popis zařízení z User-Agent hlavičky — „Firefox · Linux". */
function describeUserAgent(ua: string | null): string {
  if (!ua) return '';
  const browser = ua.includes('Edg') ? 'Edge'
    : ua.includes('Firefox') ? 'Firefox'
    : ua.includes('OPR') || ua.includes('Opera') ? 'Opera'
    : ua.includes('Chrome') ? 'Chrome'
    : ua.includes('Safari') ? 'Safari'
    : '';
  const os = ua.includes('Windows') ? 'Windows'
    : ua.includes('Android') ? 'Android'
    : ua.includes('iPhone') || ua.includes('iPad') ? 'iOS'
    : ua.includes('Mac OS') ? 'macOS'
    : ua.includes('Linux') ? 'Linux'
    : '';
  const label = [browser, os].filter(Boolean).join(' · ');
  return label || ua;
}

function AddressCard({
  addr,
  onEdit,
  onDelete,
  onSetDefault,
}: {
  addr: BusinessAddress;
  onEdit: () => void;
  onDelete: () => void;
  onSetDefault: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className={`bg-linen border rounded-rustic p-4 shadow-rustic relative flex flex-col justify-between transition min-h-[160px] ${
        addr.isDefault ? 'border-moss border-2 bg-moss/5' : 'border-wheat'
      }`}
    >
      <div>
        {/* Název adresy a badge */}
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="font-semibold text-bark text-sm truncate max-w-[50%]">
            {addr.addressName}
          </span>
          <div className="flex gap-1 items-center">
            {addr.addressType === 'BILLING' && addr.verified && (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-ochre/20 text-ochre border border-ochre/30 whitespace-nowrap">
                🔒 {t('addressVerified')}
              </span>
            )}
            {addr.isDefault ? (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-moss/20 text-moss-dark border border-moss/30 whitespace-nowrap">
                {t('addressDefault')}
              </span>
            ) : (
              <button
                onClick={onSetDefault}
                className="text-xs text-wood hover:text-moss-dark underline transition font-medium"
              >
                {t('addressSetDefault')}
              </button>
            )}
          </div>
        </div>

        {/* Detail adresy */}
        <div className="text-xs text-ink space-y-0.5">
          {addr.companyName && <p className="font-semibold">{addr.companyName}</p>}
          {(addr.firstName || addr.lastName) && (
            <p>{[addr.firstName, addr.lastName].filter(Boolean).join(' ')}</p>
          )}
          {addr.street && <p>{addr.street}</p>}
          {(addr.city || addr.zip) && (
            <p>{[addr.zip, addr.city].filter(Boolean).join(' ')}</p>
          )}
          {addr.country && <p className="text-wood">{addr.country}</p>}
          {(addr.ic || addr.dic) && (
            <p className="text-xs text-wood pt-1 font-mono">
              {addr.ic && <span>{t('icLabelShort')}: {addr.ic} </span>}
              {addr.dic && <span>{t('dicLabelShort')}: {addr.dic}</span>}
            </p>
          )}
        </div>
      </div>

      {/* Akce */}
      <div className="flex items-center justify-end gap-3 mt-4 pt-2 border-t border-wheat/40">
        {addr.addressType === 'BILLING' && addr.verified ? (
          <span className="text-xs text-wood italic">
            🔒 {t('addressVerifiedCannotChange')}
          </span>
        ) : (
          <>
            <button
              onClick={onEdit}
              className="text-xs text-wood hover:text-bark transition font-semibold"
            >
              ✏️ {t('addressEdit')}
            </button>
            <button
              onClick={onDelete}
              className="text-xs text-rust hover:text-rust-dark transition font-semibold"
            >
              🗑️ {t('addressDelete')}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function LicenseTab() {
  const { license, loading } = useLicense();
  const { t, locale } = useTranslation();
  const fmtLocale = useFormatLocale();

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString(fmtLocale);
  };

  const getDaysUntilExpiry = (expiresAt: string | null) => {
    if (!expiresAt) return null;
    const diff = new Date(expiresAt).getTime() - Date.now();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <Card>
        <Skeleton width="100%" height={200} />
      </Card>
    );
  }

  if (!license || !license.hasActiveLicense) {
    return (
      <Card>
        <h2 className="text-lg font-semibold text-bark mb-4">📋 {t('tabLicense')}</h2>
        <Alert type="error">
          {t('noActiveLicense')}
        </Alert>
      </Card>
    );
  }

  const statusColors: Record<string, string> = {
    ACTIVE: 'text-moss-dark bg-moss/10',
    EXPIRED: 'text-rust bg-rust/10',
    SUSPENDED: 'text-ochre bg-amber/10',
    CANCELLED: 'text-rust bg-rust/10',
  };

  return (
    <div className="space-y-6">
      {license.licenses.map((lic) => {
        const days = getDaysUntilExpiry(lic.expiresAt);
        return (
          <Card key={lic.licenseId}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-bark">📋 {lic.planName} ({lic.planCode})</h2>
              <span className={`inline-block px-2 py-0.5 rounded-rustic text-xs font-semibold ${statusColors[lic.status] || 'text-bark bg-wheat/20'}`}>
                {lic.status}
              </span>
            </div>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-base">
              <div>
                <dt className="text-wood">{t('licenseStart')}</dt>
                <dd className="font-medium text-bark">{formatDate(lic.startsAt)}</dd>
              </div>
              <div>
                <dt className="text-wood">{t('licenseExpiry')}</dt>
                <dd className="font-medium text-bark">
                  {formatDate(lic.expiresAt)}
                  {days !== null && days <= 7 && days > 0 && (
                    <span className="text-ochre ml-2">({days} {t('licenseDaysRemaining')})</span>
                  )}
                  {days !== null && days <= 0 && (
                    <span className="text-rust ml-2">({t('licenseExpired')})</span>
                  )}
                </dd>
              </div>
              <div>
                <dt className="text-wood">{t('licenseAutoRenew')}</dt>
                <dd className="font-medium text-bark">{lic.autoRenew ? t('yes') : t('no')}</dd>
              </div>
            </dl>
            {lic.modules.length > 0 && (
              <div className="mt-4">
                <dt className="text-wood text-sm mb-2">🧩 {t('licenseModulesInPlan')}</dt>
                <div className="flex flex-wrap gap-2">
                  {lic.modules.map((mod) => (
                    <span key={mod} className="inline-block px-3 py-1 text-xs font-medium bg-moss/10 text-moss-dark rounded-rustic border border-moss/20">
                      {mod}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Card>
        );
      })}

      {license.modules.length > 0 && (
        <Card>
          <h2 className="text-lg font-semibold text-bark mb-4">🧩 {t('licenseAllModules')}</h2>
          <div className="flex flex-wrap gap-2">
            {license.modules.map((mod) => (
              <span key={mod} className="inline-block px-3 py-1 text-xs font-medium bg-moss/10 text-moss-dark rounded-rustic border border-moss/20">
                {mod}
              </span>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

export default function ProfileContent({
  initialTab = 'user',
  readHash = true,
  onAuthRedirect,
  onNavigate,
}: ProfileContentProps) {
  const router = useRouter();
  const { isAuthenticated, mounted, logout, role, roles, permissions, setIdleTimeoutMinutes, setListDisplayMode } = useAuth();
  const { t, locale } = useTranslation();
  const fmtLocale = useFormatLocale();
  const { showToast } = useToast();
  const { theme, setTheme } = useTheme();
  const { fontSize, setFontSize } = useFontSize();
  
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const [setup, setSetup] = useState<TwoFactorSetupResponse | null>(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [processing2fa, setProcessing2fa] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisableForm, setShowDisableForm] = useState(false);

  // Sessions tab — aktivní relace (refresh tokeny per zařízení)
  const [sessions, setSessions] = useState<SessionInfo[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const [revokingSession, setRevokingSession] = useState<string | 'others' | null>(null);

  const { changeLocale } = useTranslation();
  const [mustChangePw, setMustChangePw] = useState(false);
  const [activeTab, setActiveTab] = useState<ProfileTab>(initialTab);

  // Admin vynutil změnu hesla — otevři rovnou password tab a ukaž banner
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (isMustChangePassword()) {
      setMustChangePw(true);
      setActiveTab('password');
    }
  }, []);

  // Password Validation states
  const [passwordSubmitAttempted, setPasswordSubmitAttempted] = useState(false);
  const [currentPasswordTouched, setCurrentPasswordTouched] = useState(false);
  const [newPasswordTouched, setNewPasswordTouched] = useState(false);
  const [newPasswordConfirmTouched, setNewPasswordConfirmTouched] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showNewPasswordConfirm, setShowNewPasswordConfirm] = useState(false);
  const [showDisablePassword, setShowDisablePassword] = useState(false);
  const [showGoogleSecret, setShowGoogleSecret] = useState(false);

  const isValidCurrentPassword = currentPassword.length > 0;
  const isValidNewPassword = newPassword.length >= 6;
  const isValidNewPasswordConfirm = newPasswordConfirm === newPassword;

  const showCurrentPasswordError = (currentPasswordTouched || passwordSubmitAttempted) && !isValidCurrentPassword;
  const showNewPasswordError = (newPasswordTouched || passwordSubmitAttempted) && !isValidNewPassword;
  const showNewPasswordConfirmError = (newPasswordConfirmTouched || passwordSubmitAttempted) && !isValidNewPasswordConfirm;


  // Account Tab - User Profile details state
  const [account, setAccount] = useState<AccountData>({ firstName: '', lastName: '', stripeSecretKey: '', hasStripeSecretKey: false, stripeWebhookSecret: '', hasStripeWebhookSecret: false, webhookToken: null, _maskedStripeKey: undefined, _maskedWebhookSecret: undefined });
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountSaving, setAccountSaving] = useState(false);
  const [accountMessage, setAccountMessage] = useState<string | null>(null);
  const [accountError, setAccountError] = useState<string | null>(null);

  // Account Validation states
  const [accountSubmitAttempted, setAccountSubmitAttempted] = useState(false);
  const [accountFirstNameTouched, setAccountFirstNameTouched] = useState(false);
  const [accountLastNameTouched, setAccountLastNameTouched] = useState(false);

  // Stripe Validation state
  const [stripeSubmitAttempted, setStripeSubmitAttempted] = useState(false);
  const [revealStripeKey, setRevealStripeKey] = useState(false);
  const [revealWebhookSecret, setRevealWebhookSecret] = useState(false);
  const [plainStripeKey, setPlainStripeKey] = useState<string | null>(null);
  const [plainWebhookSecret, setPlainWebhookSecret] = useState<string | null>(null);
  const [secretsLoading, setSecretsLoading] = useState(false);

  const fetchSecrets = async () => {
    if (plainStripeKey !== null) return;
    setSecretsLoading(true);
    try {
      const data = await apiFetch<{ stripeSecretKey: string; stripeWebhookSecret: string }>('/api/auth/account/secrets');
      setPlainStripeKey(data.stripeSecretKey || null);
      setPlainWebhookSecret(data.stripeWebhookSecret || null);
    } catch {
      showToast(t('failedLoadSecrets'), 'error');
    } finally {
      setSecretsLoading(false);
    }
  };

  const isValidAccountFirstName = account.firstName.trim().length > 0;
  const isValidAccountLastName = account.lastName.trim().length > 0;
  const isValidStripeKey =
    account.stripeSecretKey.trim() === '' || /^sk_(test|live)_[A-Za-z0-9]+$/.test(account.stripeSecretKey.trim());

  const showAccountFirstNameError = (accountFirstNameTouched || accountSubmitAttempted) && !isValidAccountFirstName;
  const showAccountLastNameError = (accountLastNameTouched || accountSubmitAttempted) && !isValidAccountLastName;
  const showStripeKeyError = stripeSubmitAttempted && !isValidStripeKey;

  // Google Sync state
  const [googleSyncing, setGoogleSyncing] = useState(false);
  const [googleSyncResult, setGoogleSyncResult] = useState<{ calendarSynced: number; tasksSynced: number } | null>(null);
  const [googleError, setGoogleError] = useState<string | null>(null);
  const [googleSettings, setGoogleSettings] = useState<SyncSettings | null>(null);
  const [savingGoogleSettings, setSavingGoogleSettings] = useState(false);
  const [googleCalendars, setGoogleCalendars] = useState<GoogleCalendar[]>([]);

  const { data: googleStatus, mutate: mutateGoogleStatus } = useSWR<{ connected: boolean; googleEmail: string; configured: boolean }>(
    mounted && isAuthenticated ? '/api/user/google/status' : null,
  );

  // Fetch sync settings when connected
  useEffect(() => {
    if (googleStatus?.connected && !googleSettings) {
      apiFetch<SyncSettings>('/api/user/google/sync/settings')
        .then(setGoogleSettings)
        .catch(() => {});
      apiFetch<GoogleCalendar[]>('/api/user/google/sync/calendars')
        .then(setGoogleCalendars)
        .catch(() => setGoogleCalendars([]));
    }
  }, [googleStatus?.connected, googleSettings]);

  const { data: emailAccounts } = useSWR<{ id: number; provider: string; email: string }[]>(
    mounted && isAuthenticated ? '/api/user/email/accounts' : null,
  );

  const gmailAccount = emailAccounts?.find((a) => a.provider === 'GMAIL');

  const handleSaveGoogleSettings = useCallback(async () => {
    if (!googleSettings) return;
    setGoogleError(null);
    setSavingGoogleSettings(true);
    try {
      await apiFetch('/api/user/google/sync/settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          autoSyncEnabled: googleSettings.autoSyncEnabled,
          syncIntervalMinutes: googleSettings.syncIntervalMinutes,
          syncEventTypes: googleSettings.syncEventTypes,
          googleCalendarId: googleSettings.googleCalendarId,
          syncDirection: googleSettings.syncDirection,
        }),
      });
      setGoogleSettings({ ...googleSettings });
      mutateGoogleStatus();
      showToast(t('toastUpdated'), 'success');
    } catch (err) {
      setGoogleError(err instanceof ApiError ? err.message : t('error'));
    } finally {
      setSavingGoogleSettings(false);
    }
  }, [googleSettings, mutateGoogleStatus, t]);

  const toggleGoogleEventType = (type: string) => {
    if (!googleSettings) return;
    const current = googleSettings.syncEventTypes.split(',').map(s => s.trim()).filter(Boolean);
    const next = current.includes(type) ? current.filter(t => t !== type) : [...current, type];
    setGoogleSettings({ ...googleSettings, syncEventTypes: next.join(',') });
  };

  const handleGoogleConnect = useCallback(async () => {
    setGoogleError(null);
    try {
      const result = await apiFetch<{ authUrl: string }>('/api/user/google/oauth/start');
      window.location.href = result.authUrl;
    } catch (err) {
      setGoogleError(err instanceof ApiError ? err.message : t('error'));
    }
  }, [t]);

  const handleGoogleDisconnect = useCallback(async () => {
    setGoogleError(null);
    try {
      await apiFetch('/api/user/google/disconnect', { method: 'POST' });
      mutateGoogleStatus();
      showToast(t('toastDisconnected'), 'success');
    } catch (err) {
      setGoogleError(err instanceof ApiError ? err.message : t('error'));
    }
  }, [mutateGoogleStatus, t, showToast]);

  const handleGoogleSyncNow = useCallback(async () => {
    setGoogleError(null);
    setGoogleSyncing(true);
    setGoogleSyncResult(null);
    try {
      const result = await apiFetch<{ calendarSynced: number; tasksSynced: number }>('/api/user/google/sync/now', { method: 'POST' });
      setGoogleSyncResult(result);
      mutateGoogleStatus();
      showToast(t('toastSynced'), 'success');
    } catch (err) {
      setGoogleError(err instanceof ApiError ? err.message : t('error'));
    } finally {
      setGoogleSyncing(false);
    }
  }, [mutateGoogleStatus, t, showToast]);


  // Address list states
  const [addresses, setAddresses] = useState<BusinessAddress[]>([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const addressModalExiting = useExitAnimation(showAddressModal);
  const [editingAddress, setEditingAddress] = useState<BusinessAddress | null>(null);
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState<number | null>(null);
  const [aresLoading, setAresLoading] = useState(false);
  const [viesLoading, setViesLoading] = useState(false);

  // Address Form state
  const [addressForm, setAddressForm] = useState({
    addressName: '',
    addressType: 'BILLING' as 'BILLING' | 'DELIVERY',
    companyName: '',
    firstName: '',
    lastName: '',
    street: '',
    city: '',
    zip: '',
    country: t('defaultCountry'),
    ic: '',
    dic: '',
    isDefault: false
  });

  // Validace polí adresy — zobrazujeme chyby po onBlur NEBO po pokusu o odeslání
  const [addressSubmitAttempted, setAddressSubmitAttempted] = useState(false);
  const [addressZipTouched, setAddressZipTouched] = useState(false);
  const [addressIcTouched, setAddressIcTouched] = useState(false);
  const [addressDicTouched, setAddressDicTouched] = useState(false);

  const isAddressZipValid = !addressForm.zip || /^\d{3}\s?\d{2}$/.test(addressForm.zip.trim());
  const isAddressIcValid = !addressForm.ic || /^\d{8}$/.test(addressForm.ic.trim());
  const isAddressDicValid = !addressForm.dic || /^(CZ|SK|cz|sk)?\d{8,10}$/.test(addressForm.dic.trim());

  // Zobrazit chybu pokud: pole bylo opuštěno NEBO byl pokus o odeslání
  const showZipError = (addressZipTouched || addressSubmitAttempted) && !isAddressZipValid;
  const showIcError = (addressIcTouched || addressSubmitAttempted) && !isAddressIcValid;
  const showDicError = (addressDicTouched || addressSubmitAttempted) && !isAddressDicValid;
  const showNameError = addressSubmitAttempted && !addressForm.addressName.trim();

  const tabs: { key: typeof activeTab; label: string }[] = [
    { key: 'license', label: t('tabLicense') },
    { key: 'user', label: t('tabUser') },
    { key: 'password', label: t('tabPassword') },
    { key: '2fa', label: t('tab2fa') },
    { key: 'sessions', label: t('tabSessions') },
    { key: 'notifications', label: t('tabNotifications') },
    { key: 'account', label: t('tabAccount') },
    { key: 'stripe', label: t('stripeTab') },
    ...(role === 'ADMIN' || permissions.includes('calendar.view') ? [{ key: 'google' as const, label: t('googleSync') }] : []),
    { key: 'settings', label: t('tabSettings') },
  ];

  // Otevři záložku podle URL hashe (např. /profile#settings) — pouze v page módu
  useEffect(() => {
    if (!readHash) return;
    const raw = window.location.hash.replace('#', '');
    const [hash, hashQuery] = raw.split('?');
    const validTabs: (typeof activeTab)[] = ['user', 'password', '2fa', 'sessions', 'notifications', 'account', 'stripe', 'google', 'license', 'settings'];
    if (validTabs.includes(hash as typeof activeTab)) {
      setActiveTab(hash as typeof activeTab);
    }
    const q = new URLSearchParams(hashQuery ?? '');
    if (q.get('success') === 'true') showToast(t('googleConnectSuccess'), 'success');
    else if (q.get('error')) showToast(t('googleConnectError'), 'error');
  }, [readHash]);

  const fetchProfile = useCallback(async () => {
    try {
      const data = await apiFetch<UserProfile>('/api/auth/me');
      setProfile(data);
    } catch (e: any) {
      const msg = e?.message || t('profileLoadFailed');
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [t, showToast]);

  const fetchAddresses = useCallback(async () => {
    setAddressesLoading(true);
    try {
      const data = await apiFetch<BusinessAddress[]>('/api/user/stripe/addresses');
      setAddresses(data);
    } catch (e: any) {
      showToast(e?.message || t('errLoadAddresses'), 'error');
    } finally {
      setAddressesLoading(false);
    }
  }, [showToast]);

  const fetchSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const data = await apiFetch<SessionInfo[]>('/api/auth/sessions');
      setSessions(data);
      setSessionsLoaded(true);
    } catch (e: any) {
      showToast(e?.message || t('sessionsLoadFailed'), 'error');
    } finally {
      setSessionsLoading(false);
    }
  }, [showToast, t]);

  const onRevokeSession = async (sessionId: string) => {
    if (revokingSession) return;
    setRevokingSession(sessionId);
    try {
      const res = await apiFetch<{ wasCurrent: boolean }>(`/api/auth/sessions/${encodeURIComponent(sessionId)}`, { method: 'DELETE' });
      if (res.wasCurrent) {
        await logout();
        return;
      }
      setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
      showToast(t('sessionRevoked'), 'success');
    } catch (e: any) {
      showToast(e?.message || t('sessionRevokeFailed'), 'error');
    } finally {
      setRevokingSession(null);
    }
  };

  const onRevokeOtherSessions = async () => {
    if (revokingSession) return;
    setRevokingSession('others');
    try {
      const res = await apiFetch<{ revokedCount: number }>('/api/auth/sessions/others/all', { method: 'DELETE' });
      setSessions((prev) => prev.filter((s) => s.current));
      showToast(t('sessionOthersRevoked').replace('{n}', String(res.revokedCount)), 'success');
    } catch (e: any) {
      showToast(e?.message || t('sessionRevokeFailed'), 'error');
    } finally {
      setRevokingSession(null);
    }
  };

  useEffect(() => {
    if (!mounted) return;
    if (!isAuthenticated) {
      if (onAuthRedirect) {
        onAuthRedirect();
      } else {
        router.push('/login');
      }
      return;
    }
    fetchProfile();
  }, [mounted, isAuthenticated, router, fetchProfile, onAuthRedirect]);

  useEffect(() => {
    if ((activeTab !== 'user' && activeTab !== 'account' && activeTab !== 'stripe' && activeTab !== 'license') || !isAuthenticated) return;
    setAccountLoading(true);
    // Fetch personal details (firstName, lastName, Stripe key)
    apiFetch<AccountData>('/api/auth/account')
      .then((data) => setAccount({
        firstName: data.firstName ?? '',
        lastName: data.lastName ?? '',
        stripeSecretKey: '',
        hasStripeSecretKey: data.hasStripeSecretKey ?? false,
        stripeWebhookSecret: '',
        hasStripeWebhookSecret: data.hasStripeWebhookSecret ?? false,
        webhookToken: data.webhookToken ?? null,
        _maskedStripeKey: data.stripeSecretKey ?? undefined,
        _maskedWebhookSecret: data.stripeWebhookSecret ?? undefined,
      }))
      .catch((e: any) => {
        const msg = e?.message || t('loadDataFailed');
        setAccountError(msg);
        showToast(msg, 'error');
      })
      .finally(() => setAccountLoading(false));

    // Fetch multiple addresses
    fetchAddresses();
  }, [activeTab, isAuthenticated, t, showToast, fetchAddresses]);

  useEffect(() => {
    if (activeTab === 'sessions' && isAuthenticated && !sessionsLoaded) {
      fetchSessions();
    }
  }, [activeTab, isAuthenticated, sessionsLoaded, fetchSessions]);

  const onSaveAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setAccountSubmitAttempted(true);
    if (!isValidAccountFirstName || !isValidAccountLastName) {
      return;
    }
    setAccountSaving(true);
    setAccountMessage(null);
    setAccountError(null);
    try {
      const payload = {
        firstName: account.firstName,
        lastName: account.lastName,
      };
      const data = await apiFetch<AccountData>('/api/auth/account', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      const msg = t('saveDataOk');
      setAccountMessage(msg);
      showToast(msg, 'success');
      setAccountSubmitAttempted(false);
      setAccountFirstNameTouched(false);
      setAccountLastNameTouched(false);

      if (typeof window !== 'undefined') {
        window.localStorage.setItem('firstName', account.firstName);
        window.localStorage.setItem('lastName', account.lastName);
        window.dispatchEvent(new Event('storage'));
      }

      // Update profile name dynamically
      fetchProfile();
    } catch (e: any) {
      const msg = e?.message || t('saveDataFailed');
      setAccountError(msg);
      showToast(msg, 'error');
    } finally {
      setAccountSaving(false);
    }
  };

  const onSaveStripe = async (e: React.FormEvent) => {
    e.preventDefault();
    setStripeSubmitAttempted(true);
    if (!isValidStripeKey) {
      return;
    }
    setAccountSaving(true);
    setAccountMessage(null);
    setAccountError(null);
    try {
      const payload = {
        firstName: account.firstName,
        lastName: account.lastName,
        stripeSecretKey: account.stripeSecretKey.trim() || undefined,
        stripeWebhookSecret: account.stripeWebhookSecret.trim() || undefined,
      };
      const data = await apiFetch<AccountData>('/api/auth/account', {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      const msg = t('stripeConfigSaved');
      setAccountMessage(msg);
      showToast(msg, 'success');
      setStripeSubmitAttempted(false);

      setAccount((s) => ({
        ...s,
        stripeSecretKey: '',
        hasStripeSecretKey: data.hasStripeSecretKey,
        _maskedStripeKey: data.stripeSecretKey ?? s._maskedStripeKey,
        stripeWebhookSecret: '',
        hasStripeWebhookSecret: data.hasStripeWebhookSecret,
        _maskedWebhookSecret: data.stripeWebhookSecret ?? s._maskedWebhookSecret,
        webhookToken: data.webhookToken ?? s.webhookToken,
      }));
    } catch (e: any) {
      const msg = e?.message || t('saveDataFailed');
      setAccountError(msg);
      showToast(msg, 'error');
    } finally {
      setAccountSaving(false);
    }
  };

  const onChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordSubmitAttempted(true);
    setError(null);
    setMessage(null);
    if (!isValidCurrentPassword || !isValidNewPassword || !isValidNewPasswordConfirm) {
      return;
    }
    setChangingPassword(true);
    try {
      await apiFetch<{ message: string }>('/api/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const msg = t('passwordChangedOk');
      setMessage(msg);
      showToast(msg, 'success');
      clearMustChangePassword();
      setMustChangePw(false);
      setCurrentPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');
      setPasswordSubmitAttempted(false);
      setCurrentPasswordTouched(false);
      setNewPasswordTouched(false);
      setNewPasswordConfirmTouched(false);
    } catch (e: any) {
      const msg = e?.message || t('changePasswordFailed');
      setError(msg);
      showToast(msg, 'error');
    } finally {
      setChangingPassword(false);
    }
  };

  // 2FA Methods
  const onSetup2FA = async () => {
    setProcessing2fa(true);
    setError(null);
    try {
      const data = await apiFetch<TwoFactorSetupResponse>('/api/auth/2fa/setup', { method: 'POST' });
      setSetup(data);
    } catch (e: any) {
      showToast(e?.message || t('twoFactorSetupError'), 'error');
    } finally {
      setProcessing2fa(false);
    }
  };

  const onVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setProcessing2fa(true);
    setError(null);
    try {
      await apiFetch<any>('/api/auth/2fa/verify', {
        method: 'POST',
        body: JSON.stringify({ code: twoFactorCode }),
      });
      showToast(t('twoFactorSetupOk'), 'success');
      setSetup(null);
      setTwoFactorCode('');
      fetchProfile();
    } catch (e: any) {
      showToast(e?.message || t('errInvalidCode'), 'error');
    } finally {
      setProcessing2fa(false);
    }
  };

  const onDisable2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setProcessing2fa(true);
    try {
      await apiFetch<any>('/api/auth/2fa/disable', {
        method: 'POST',
        body: JSON.stringify({ currentPassword: disablePassword }),
      });
      showToast(t('twoFactorDisabledOk2'), 'success');
      setShowDisableForm(false);
      setDisablePassword('');
      fetchProfile();
    } catch (e: any) {
      showToast(e?.message || t('err2faDisable'), 'error');
    } finally {
      setProcessing2fa(false);
    }
  };

  // Address CRUD actions
  const openAddressModal = (addr: BusinessAddress | null = null, defaultType?: 'BILLING' | 'DELIVERY') => {
    setEditingAddress(addr);
    setAddressSubmitAttempted(false);
    setAddressZipTouched(false);
    setAddressIcTouched(false);
    setAddressDicTouched(false);

    if (addr) {
      setAddressForm({
        addressName: addr.addressName,
        addressType: addr.addressType,
        companyName: addr.companyName ?? '',
        firstName: addr.firstName ?? '',
        lastName: addr.lastName ?? '',
        street: addr.street ?? '',
        city: addr.city ?? '',
        zip: addr.zip ?? '',
        country: addr.country || t('defaultCountry'),
        ic: addr.ic ?? '',
        dic: addr.dic ?? '',
        isDefault: addr.isDefault
      });
    } else {
      setAddressForm({
        addressName: '',
        addressType: defaultType || 'BILLING',
        companyName: '',
        firstName: '',
        lastName: '',
        street: '',
        city: '',
        zip: '',
        country: t('defaultCountry'),
        ic: '',
        dic: '',
        isDefault: false
      });
    }
    setShowAddressModal(true);
  };

  const handleAresLookup = async () => {
    const icVal = addressForm.ic.trim();
    if (!icVal) {
      showToast(t('aresEnterIc'), 'error');
      return;
    }
    setAresLoading(true);
    try {
      const data = await apiFetch<{
        ic: string;
        companyName: string;
        street: string;
        city: string;
        zip: string;
        country: string;
        dic: string | null;
      }>(`/api/user/ares?ic=${icVal}`);

      setAddressForm(s => ({
        ...s,
        companyName: data.companyName || s.companyName,
        street: data.street || s.street,
        city: data.city || s.city,
        zip: data.zip || s.zip,
        country: data.country || s.country,
        dic: data.dic || s.dic || '',
      }));

      setAddressZipTouched(true);
      setAddressIcTouched(true);
      setAddressDicTouched(true);
      showToast(t('aresDataLoaded'), 'success');
    } catch (e: any) {
      showToast(e?.message || t('errAresLookup'), 'error');
    } finally {
      setAresLoading(false);
    }
  };

  const handleViesLookup = async () => {
    const dicVal = addressForm.dic.trim();
    if (!dicVal) {
      showToast(t('aresEnterDic'), 'error');
      return;
    }
    let countryCode = 'CZ';
    let vatNumber = dicVal;
    if (dicVal.length > 2 && /^[a-zA-Z]{2}/.test(dicVal)) {
      countryCode = dicVal.substring(0, 2).toUpperCase();
      vatNumber = dicVal.substring(2);
    }
    
    setViesLoading(true);
    try {
      const data = await apiFetch<{
        isValid: boolean;
        name: string;
        address: string;
        vatNumber: string;
      }>(`/api/user/stripe/addresses/vies?country=${countryCode}&vat=${vatNumber}`);

      if (data.isValid) {
        showToast(
          `${t('viesValidPrefix')}${data.name || t('unknownName')}`,
          'success'
        );
        setAddressForm(s => ({
          ...s,
          companyName: s.companyName || data.name || '',
          dic: data.vatNumber || s.dic,
        }));
      } else {
        showToast(t('viesInvalid'), 'info');
      }
      setAddressDicTouched(true);
    } catch (e: any) {
      showToast(e?.message || t('errViesLookup'), 'error');
    } finally {
      setViesLoading(false);
    }
  };

  const handleSaveAddress = async (e: React.FormEvent) => {
    e.preventDefault();
    // Při pokusu o odeslání zobrazíme chyby u VŠECH neplatných polí najednou
    setAddressSubmitAttempted(true);
    if (!isAddressZipValid || !isAddressIcValid || !isAddressDicValid || !addressForm.addressName.trim()) {
      return; // zastavit — uživatel vidí všechny chybné políčka
    }
    setAddressSaving(true);
    try {
      const url = editingAddress 
        ? `/api/user/stripe/addresses/${editingAddress.id}` 
        : '/api/user/stripe/addresses';
      const method = editingAddress ? 'PUT' : 'POST';

      await apiFetch<BusinessAddress>(url, {
        method,
        body: JSON.stringify(addressForm),
      });

      showToast(t('addressSaved'), 'success');
      setShowAddressModal(false);
      fetchAddresses();
    } catch (e: any) {
      showToast(e?.message || t('errSaveAddress'), 'error');
    } finally {
      setAddressSaving(false);
    }
  };

  const handleDeleteAddress = async (id: number) => {
    try {
      await apiFetch(`/api/user/stripe/addresses/${id}`, { method: 'DELETE' });
      showToast(t('addressDeleted'), 'success');
      fetchAddresses();
    } catch (e: any) {
      showToast(e?.message || t('errDeleteAddress'), 'error');
    }
  };

  const handleSetDefaultAddress = async (id: number) => {
    try {
      await apiFetch(`/api/user/stripe/addresses/${id}/default`, { method: 'PATCH' });
      showToast(t('addressDefaultChanged'), 'success');
      fetchAddresses();
    } catch (e: any) {
      showToast(e?.message || t('errDefaultAddress'), 'error');
    }
  };

  if (!mounted) {
    return (
      <div className="max-w-2xl mx-auto space-y-4 p-4">
        <Skeleton height={40} width="60%" />
        <Skeleton height={200} width="100%" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex flex-col sm:flex-row gap-6">
        {/* Vertical tab sidebar */}
        <nav className="sm:w-48 sm:flex-shrink-0" aria-label={t('profileTabs')}>
          <div className="flex sm:flex-col gap-1 overflow-x-auto sm:overflow-visible pb-2 sm:pb-0 border-b sm:border-b-0 sm:border-r border-wheat pr-2">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  setActiveTab(tab.key);
                  setPasswordSubmitAttempted(false);
                  setCurrentPasswordTouched(false);
                  setNewPasswordTouched(false);
                  setNewPasswordConfirmTouched(false);
                  setAccountSubmitAttempted(false);
                  setAccountFirstNameTouched(false);
                  setAccountLastNameTouched(false);
                  setStripeSubmitAttempted(false);
                }}
                className={`px-3 py-2 text-sm font-medium rounded-rustic whitespace-nowrap transition text-left ${
                  activeTab === tab.key
                    ? 'bg-moss/10 text-moss-dark border-l-2 border-moss'
                    : 'text-wood hover:text-bark hover:bg-cream/50 border-l-2 border-transparent'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </nav>

        {/* Tab content */}
        <div className="flex-1 min-w-0">

      {activeTab === 'user' && (
        <div className="space-y-6">
          <Card>
            <h2 className="text-lg font-semibold text-bark mb-4">{t('userDataTitle')}</h2>
            {profile && (
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-base">
                <div>
                  <dt className="text-wood">{t('email')}</dt>
                  <dd className="font-medium text-bark">{profile.email}</dd>
                </div>
                <div>
                  <dt className="text-wood">{t('usernameLabel')}</dt>
                  <dd className="font-medium text-bark">{profile.username || '—'}</dd>
                </div>
                <div>
                  <dt className="text-wood">{t('roleLabel')}</dt>
                  <dd className="font-medium text-bark capitalize">{profile.role}</dd>
                </div>
                {roles.length > 0 && (
                  <div className="sm:col-span-2">
                    <dt className="text-wood">{t('yourRoles')}</dt>
                    <dd className="mt-1">
                      <RoleDescriptions roleNames={roles} />
                    </dd>
                  </div>
                )}
                <div>
                  <dt className="text-wood">{t('registeredLabel')}</dt>
                  <dd className="font-medium text-bark">
                    {new Date(profile.createdAt).toLocaleString(fmtLocale)}
                  </dd>
                </div>
                <div>
                  <dt className="text-wood">{t('twoFactorLabel')}</dt>
                  <dd className="font-medium text-bark">
                    {profile.twoFactorEnabled ? t('twoFactorOn') : t('twoFactorOff')}
                  </dd>
                </div>
              </dl>
            )}
          </Card>

          <Card>
            <h2 className="text-lg font-semibold text-bark mb-4">👤 {t('personalProfile')}</h2>
            {accountLoading ? (
              <Skeleton width="100%" height={80} />
            ) : (
              <form onSubmit={onSaveAccount} className={`space-y-4 ${accountSaving ? 'cursor-wait' : ''}`}>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="field-label" htmlFor="profileFirstName">{t('firstName')}</label>
                    <input
                      id="profileFirstName"
                      type="text"
                      readOnly={role !== 'ADMIN'}
                      className={`field-input ${showAccountFirstNameError ? 'field-input-invalid' : ''} ${role !== 'ADMIN' ? 'bg-cream/50 text-wood cursor-not-allowed' : ''}`}
                      value={account.firstName}
                      onChange={(e) => {
                        setAccount((s) => ({ ...s, firstName: e.target.value }));
                        setAccountFirstNameTouched(true);
                      }}
                      onBlur={() => setAccountFirstNameTouched(true)}
                    />
                    {showAccountFirstNameError && (
                      <p className="field-error-text">{t('fillOutField')}</p>
                    )}
                  </div>
                  <div>
                    <label className="field-label" htmlFor="profileLastName">{t('lastName')}</label>
                    <input
                      id="profileLastName"
                      type="text"
                      readOnly={role !== 'ADMIN'}
                      className={`field-input ${showAccountLastNameError ? 'field-input-invalid' : ''} ${role !== 'ADMIN' ? 'bg-cream/50 text-wood cursor-not-allowed' : ''}`}
                      value={account.lastName}
                      onChange={(e) => {
                        setAccount((s) => ({ ...s, lastName: e.target.value }));
                        setAccountLastNameTouched(true);
                      }}
                      onBlur={() => setAccountLastNameTouched(true)}
                    />
                    {showAccountLastNameError && (
                      <p className="field-error-text">{t('fillOutField')}</p>
                    )}
                  </div>
                </div>

                {accountMessage && <Alert type="success">{accountMessage}</Alert>}
                {accountError && <Alert type="error">{accountError}</Alert>}

                {role === 'ADMIN' && (
                <div className="flex justify-end">
                  <button type="submit" className="btn-primary" disabled={accountSaving}>
                    {accountSaving ? t('saving') : t('saveData')}
                  </button>
                </div>
                )}
              </form>
            )}
          </Card>
        </div>
      )}

      {activeTab === 'password' && (
        <Card>
          {mustChangePw && (
            <div className="mb-4 rounded-rustic border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              ⚠ {t('mustChangePasswordBanner')}
            </div>
          )}
          <h2 className="text-lg font-semibold text-bark mb-4">{t('changePasswordTitle')}</h2>
          <form onSubmit={onChangePassword} className={`grid gap-4 ${changingPassword ? 'cursor-wait' : ''}`} noValidate>
            <div>
              <label className="field-label" htmlFor="currentPassword">{t('currentPassword')}</label>
              <div className="relative">
                <input
                  id="currentPassword"
                  type={showCurrentPassword ? 'text' : 'password'}
                  className={`field-input pr-10 ${showCurrentPasswordError ? 'field-input-invalid' : ''}`}
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => {
                    setCurrentPassword(e.target.value);
                    setCurrentPasswordTouched(true);
                  }}
                  onBlur={() => setCurrentPasswordTouched(true)}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-wood hover:text-bark text-sm"
                  tabIndex={-1}
                >
                  {showCurrentPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {showCurrentPasswordError && (
                <p className="field-error-text">{t('fillOutField')}</p>
              )}
            </div>
            <div>
              <label className="field-label" htmlFor="newPassword">{t('newPassword')}</label>
              <div className="relative">
                <input
                  id="newPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  className={`field-input pr-10 ${showNewPasswordError ? 'field-input-invalid' : ''}`}
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setNewPasswordTouched(true);
                  }}
                  onBlur={() => setNewPasswordTouched(true)}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-wood hover:text-bark text-sm"
                  tabIndex={-1}
                >
                  {showNewPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {showNewPasswordError && (
                <p className="field-error-text">{t('passwordTooShortHelp')}</p>
              )}
              <PasswordStrength password={newPassword} />
            </div>
            <div>
              <label className="field-label" htmlFor="newPasswordConfirm">{t('newPasswordConfirm')}</label>
              <div className="relative">
                <input
                  id="newPasswordConfirm"
                  type={showNewPasswordConfirm ? 'text' : 'password'}
                  className={`field-input pr-10 ${showNewPasswordConfirmError ? 'field-input-invalid' : ''}`}
                  autoComplete="new-password"
                  value={newPasswordConfirm}
                  onChange={(e) => {
                    setNewPasswordConfirm(e.target.value);
                    setNewPasswordConfirmTouched(true);
                  }}
                  onBlur={() => setNewPasswordConfirmTouched(true)}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPasswordConfirm(!showNewPasswordConfirm)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-wood hover:text-bark text-sm"
                  tabIndex={-1}
                >
                  {showNewPasswordConfirm ? '🙈' : '👁️'}
                </button>
              </div>
              {showNewPasswordConfirmError && (
                <p className="field-error-text">{t('passwordsDoNotMatch')}</p>
              )}
            </div>
            <button type="submit" className="btn-primary" disabled={changingPassword}>
              {changingPassword ? t('changingPassword') : t('changePasswordBtn')}
            </button>
          </form>
        </Card>
      )}

      {activeTab === '2fa' && (
        <Card>
          <h2 className="text-lg font-semibold text-bark mb-4">{t('twoFactorTitle')}</h2>
          {profile?.twoFactorEnabled ? (
            <div className="space-y-4">
              <p className="text-moss-dark font-medium">{t('twoFaEnabled')}</p>
              {!showDisableForm ? (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowDisableForm(true)}
                >
                  {t('disable2fa')}
                </button>
              ) : (
                <form onSubmit={onDisable2FA} className="grid gap-4">
                  <div>
                    <label className="field-label" htmlFor="disablePassword">{t('confirmWithPassword')}</label>
                    <div className="relative">
                      <input
                        id="disablePassword"
                        type={showDisablePassword ? 'text' : 'password'}
                        className="field-input pr-10"
                        autoComplete="current-password"
                        value={disablePassword}
                        onChange={(e) => setDisablePassword(e.target.value)}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowDisablePassword(!showDisablePassword)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-wood hover:text-bark text-sm"
                        tabIndex={-1}
                      >
                        {showDisablePassword ? '🙈' : '👁️'}
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button type="submit" className="btn-secondary" disabled={processing2fa}>
                      {processing2fa ? t('disabling2fa') : t('confirmDisable')}
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => { setShowDisableForm(false); setDisablePassword(''); }}
                    >
                      {t('cancel')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          ) : setup ? (
            <form onSubmit={onVerify2FA} className="grid gap-4">
              <p className="text-sm text-wood">
                {t('setup2faDescription')}
              </p>
              <div className="flex justify-center">
                <Image src={setup.qrCodeUrl} alt={t('twoFactorTitle')} width={200} height={200} unoptimized className="rounded-rustic border border-wheat" />
              </div>
              <div>
                <label className="field-label">{t('secretKeyLabel')}</label>
                <input
                  type="text"
                  readOnly
                  value={setup.secret}
                  className="field-input font-mono text-sm"
                  onFocus={(e) => e.target.select()}
                />
              </div>
              <div>
                <label className="field-label" htmlFor="twoFactorCode">{t('verificationCodeLabel')}</label>
                <input
                  id="twoFactorCode"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className="field-input"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value.replace(/\D/g, ''))}
                  required
                  maxLength={6}
                  placeholder="123456"
                />
              </div>
              <div className="flex gap-3">
                <button type="submit" className="btn-primary" disabled={processing2fa}>
                  {processing2fa ? t('verifying') : t('enable2fa')}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => { setSetup(null); setTwoFactorCode(''); }}
                >
                  {t('cancel')}
                </button>
              </div>
            </form>
          ) : (
            <div>
              <p className="text-wood text-sm mb-4">{t('twoFaDisabled')}</p>
              <button type="button" className="btn-primary" onClick={onSetup2FA} disabled={processing2fa}>
                {processing2fa ? t('preparing2fa') : t('setup2fa')}
              </button>
            </div>
          )}
        </Card>
      )}

      {activeTab === 'sessions' && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-bark">{t('sessionsTitle')}</h2>
            {sessions.filter((s) => !s.current).length > 0 && (
              <button
                type="button"
                className="btn-secondary text-sm"
                onClick={onRevokeOtherSessions}
                disabled={revokingSession !== null}
              >
                {t('sessionRevokeOthers')}
              </button>
            )}
          </div>
          {sessionsLoading && !sessionsLoaded ? (
            <p className="text-wood text-sm">{t('loading')}</p>
          ) : sessions.length === 0 ? (
            <p className="text-wood text-sm">{t('sessionNoSessions')}</p>
          ) : (
            <div className="grid gap-3">
              {sessions.map((s) => (
                <div
                  key={s.sessionId}
                  className={`rounded-rustic border px-4 py-3 ${s.current ? 'border-moss/50 bg-moss/5' : 'border-wheat bg-cream/60'}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-bark">
                          {describeUserAgent(s.userAgent) || t('sessionUnknownDevice')}
                        </span>
                        {s.current && (
                          <span className="inline-block bg-moss text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                            {t('sessionCurrent')}
                          </span>
                        )}
                      </div>
                      {s.ipAddress && (
                        <div className="text-sm text-wood mt-0.5">IP: {s.ipAddress}</div>
                      )}
                      <div className="text-xs text-wood/80 mt-1 grid gap-0.5">
                        <span>{t('sessionLastActive')}: {formatDateTimeDisplay(s.lastActiveAt, fmtLocale)}</span>
                        {String(s.firstSeenAt) !== String(s.lastActiveAt) && (
                          <span>{t('sessionFirstSeen')}: {formatDateTimeDisplay(s.firstSeenAt, fmtLocale)}</span>
                        )}
                        <span>{t('sessionExpires')}: {formatDateTimeDisplay(s.expiresAt, fmtLocale)}</span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className={`btn-secondary text-sm shrink-0 ${s.current ? 'opacity-70' : ''}`}
                      onClick={() => onRevokeSession(s.sessionId)}
                      disabled={revokingSession !== null}
                      title={s.current ? t('sessionRevokeCurrentHint') : t('sessionRevoke')}
                    >
                      {revokingSession === s.sessionId ? '…' : t('sessionRevoke')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {activeTab === 'notifications' && (
        <UserNotificationPrefs />
      )}

      {activeTab === 'account' && (
        <div className="space-y-6">
          {/* Fakturační a doručovací adresy */}
          <div className="space-y-6">
            {/* Hlavička */}
            <div className="flex items-center justify-between border-b border-wheat/20 pb-2">
              <h2 className="text-lg font-semibold text-bark">🏢 {t('billingSectionTitle')} &amp; 🚚 {t('deliverySectionTitle')}</h2>
            </div>

            {addressesLoading ? (
              <div className="grid sm:grid-cols-2 gap-4">
                <Skeleton height={150} />
                <Skeleton height={150} />
              </div>
            ) : (
              <>
                {/* --- Fakturační adresy --- */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between pb-1 border-b border-rust/10">
                    <h3 className="text-sm font-bold text-rust uppercase tracking-wide flex items-center gap-2">
                      🧾 {t('billingSectionTitle')}
                    </h3>
                    <button
                      type="button"
                      onClick={() => openAddressModal(null, 'BILLING')}
                      className="btn-primary py-1.5 px-2.5 font-semibold"
                    >
                      ➕ {t('addBillingAddress')}
                    </button>
                  </div>
                  {addresses.filter(a => a.addressType === 'BILLING').length === 0 ? (
                    <Card className="p-6 text-center text-wood text-sm">
                      <p>{t('noBillingAddresses')}</p>
                    </Card>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-4">
                      {addresses.filter(a => a.addressType === 'BILLING').map((addr) => (
                        <AddressCard
                          key={addr.id}
                          addr={addr}
                          onEdit={() => openAddressModal(addr)}
                          onDelete={() => setAddressToDelete(addr.id)}
                          onSetDefault={() => handleSetDefaultAddress(addr.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* --- Doručovací adresy --- */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between pb-1 border-b border-moss/10">
                    <h3 className="text-sm font-bold text-moss uppercase tracking-wide flex items-center gap-2">
                      🚚 {t('deliverySectionTitle')}
                    </h3>
                    <button
                      type="button"
                      onClick={() => openAddressModal(null, 'DELIVERY')}
                      className="btn-primary py-1.5 px-2.5 font-semibold"
                    >
                      ➕ {t('addDeliveryAddress')}
                    </button>
                  </div>
                  {addresses.filter(a => a.addressType === 'DELIVERY').length === 0 ? (
                    <Card className="p-6 text-center text-wood text-sm">
                      <p>{t('noDeliveryAddresses')}</p>
                    </Card>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-4">
                      {addresses.filter(a => a.addressType === 'DELIVERY').map((addr) => (
                        <AddressCard
                          key={addr.id}
                          addr={addr}
                          onEdit={() => openAddressModal(addr)}
                          onDelete={() => setAddressToDelete(addr.id)}
                          onSetDefault={() => handleSetDefaultAddress(addr.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {activeTab === 'stripe' && (
        <Card>
          <h2 className="text-lg font-semibold text-bark mb-4">{t('stripeApiConfig')}</h2>
          {accountLoading ? (
            <Skeleton width="100%" height={80} />
          ) : (
            <form onSubmit={onSaveStripe} className={`space-y-4 ${accountSaving ? 'cursor-wait' : ''}`}>
              <div>
                <label className="field-label" htmlFor="profileStripeKey">
                  {t('stripeApiKeyLabel')}
                  {account.hasStripeSecretKey && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-moss/20 px-2 py-0.5 text-xs font-medium text-moss-dark">
                      {t('saved')}
                    </span>
                  )}
                </label>
                <div className="flex gap-2">
                  <input
                    id="profileStripeKey"
                    type={revealStripeKey ? 'text' : 'password'}
                    autoComplete="off"
                    className={`field-input flex-1 ${showStripeKeyError ? 'field-input-invalid' : ''}`}
                    placeholder={account._maskedStripeKey || t('stripeApiKeyPlaceholder')}
                    value={account.stripeSecretKey}
                    onChange={(e) => setAccount((s) => ({ ...s, stripeSecretKey: e.target.value }))}
                  />
                  <button
                    type="button"
                    onClick={async () => { await fetchSecrets(); setRevealStripeKey((v) => !v); }}
                    className="btn-secondary px-3 shrink-0"
                    title={revealStripeKey ? t('hide') : t('show')}
                    disabled={secretsLoading}
                  >
                    {secretsLoading ? '⏳' : revealStripeKey ? '🙈' : '👁️'}
                  </button>
                </div>
                {revealStripeKey && plainStripeKey && (
                  <p className="mt-1 text-xs font-mono text-ink break-all bg-wheat/40 rounded px-2 py-1">{plainStripeKey}</p>
                )}
                {showStripeKeyError && (
                  <p className="field-error-text">{t('stripeApiKeyRequired')}</p>
                )}
              </div>

              <div>
                <label className="field-label" htmlFor="profileStripeWebhookSecret">
                  {t('stripeWebhookSecretLabel')}
                  {account.hasStripeWebhookSecret && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-moss/20 px-2 py-0.5 text-xs font-medium text-moss-dark">
                      {t('saved')}
                    </span>
                  )}
                </label>
                <div className="flex gap-2">
                  <input
                    id="profileStripeWebhookSecret"
                    type={revealWebhookSecret ? 'text' : 'password'}
                    autoComplete="off"
                    className="field-input flex-1"
                    placeholder={account._maskedWebhookSecret || 'whsec_...'}
                    value={account.stripeWebhookSecret}
                    onChange={(e) => setAccount((s) => ({ ...s, stripeWebhookSecret: e.target.value }))}
                  />
                  <button
                    type="button"
                    onClick={async () => { await fetchSecrets(); setRevealWebhookSecret((v) => !v); }}
                    className="btn-secondary px-3 shrink-0"
                    title={revealWebhookSecret ? t('hide') : t('show')}
                    disabled={secretsLoading}
                  >
                    {secretsLoading ? '⏳' : revealWebhookSecret ? '🙈' : '👁️'}
                  </button>
                </div>
                {revealWebhookSecret && plainWebhookSecret && (
                  <p className="mt-1 text-xs font-mono text-ink break-all bg-wheat/40 rounded px-2 py-1">{plainWebhookSecret}</p>
                )}
                <p className="text-xs text-wood mt-1">
                  {t('webhookSecretHelp')}
                </p>
              </div>

              {account.webhookToken && (
                <div className="rounded-rustic border border-wheat bg-linen p-3 space-y-1">
                  <p className="text-xs font-semibold text-bark">
                    {t('webhookUrlLabel')}
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs text-moss-dark font-mono break-all flex-1">
                      {typeof window !== 'undefined' ? `${window.location.origin}/api/webhooks/stripe/${account.webhookToken}` : `/api/webhooks/stripe/${account.webhookToken}`}
                    </code>
                    <button
                      type="button"
                      className="btn-secondary px-2 py-1 shrink-0"
                      onClick={() => {
                        const url = `${window.location.origin}/api/webhooks/stripe/${account.webhookToken}`;
                        navigator.clipboard.writeText(url);
                        showToast(t('urlCopied'), 'success');
                      }}
                    >
                      📋
                    </button>
                  </div>
                </div>
              )}

              {accountMessage && <Alert type="success">{accountMessage}</Alert>}
              {accountError && <Alert type="error">{accountError}</Alert>}

              <div className="flex justify-end">
                <button type="submit" className="btn-primary" disabled={accountSaving}>
                  {accountSaving ? t('saving') : t('saveData')}
                </button>
              </div>
            </form>
          )}
        </Card>
      )}

      {activeTab === 'google' && (role === 'ADMIN' || permissions.includes('calendar.view')) && (
        <Card>
          <h2 className="text-lg font-semibold text-bark mb-4">{t('googleAuthTitle')}</h2>

          {googleError && (
            <div className="bg-rust/10 border border-rust/30 rounded-rustic p-3 mb-4">
              <p className="text-rust text-sm">{googleError}</p>
            </div>
          )}

          {googleSyncResult && (
            <div className="bg-moss/10 border border-moss/30 rounded-rustic p-3 mb-4">
              <p className="text-moss-dark text-sm font-medium">{t('googleAuthSyncComplete')}</p>
              <p className="text-xs text-wood mt-1">
                {t('googleAuthCalendarSynced')}: {googleSyncResult.calendarSynced} |
                {' '}{t('googleAuthTasksSynced')}: {googleSyncResult.tasksSynced}
              </p>
            </div>
          )}

          {!(googleStatus?.configured ?? false) && (
            <div className="bg-ochre/20 border border-ochre/30 rounded-rustic p-3 mb-4">
              <p className="text-ochre text-sm">
                {t('googleAuthNotConfiguredAdmin')}
              </p>
            </div>
          )}

          <div className="flex items-center gap-3 mb-4">
            <div className={`w-3 h-3 rounded-full ${googleStatus?.connected ? 'bg-moss' : 'bg-rust'}`} />
            <div>
              <p className="font-semibold text-bark text-sm">
                {googleStatus?.connected
                  ? (t('googleAuthConnected'))
                  : (t('googleAuthNotConnected'))}
              </p>
              {googleStatus?.connected && googleStatus?.googleEmail && (
                <p className="text-xs text-wood">{googleStatus.googleEmail}</p>
              )}
            </div>
          </div>

          <p className="text-sm text-wood mb-4">
            {t('googleAuthDescription')}
          </p>

          <div className="flex gap-3 flex-wrap">
            {!googleStatus?.connected && (googleStatus?.configured ?? false) ? (
              <button
                onClick={handleGoogleConnect}
                className="px-4 py-2 bg-moss text-cream rounded-rustic font-medium hover:bg-moss-dark transition"
              >
                {t('googleAuthConnect')}
              </button>
            ) : googleStatus?.connected ? (
              <>
                <button
                  onClick={handleGoogleSyncNow}
                  disabled={googleSyncing}
                  className="px-4 py-2 bg-wood text-cream rounded-rustic font-medium hover:bg-bark transition disabled:opacity-50"
                >
                  {googleSyncing ? (t('googleAuthSyncing')) : (t('googleAuthSyncNow'))}
                </button>
                <button
                  onClick={handleGoogleDisconnect}
                  className="px-4 py-2 bg-rust text-cream rounded-rustic font-medium hover:bg-rust-dark transition"
                >
                  {t('googleAuthDisconnect')}
                </button>
              </>
            ) : null}
          </div>

          {googleStatus?.connected && googleSettings && (
            <div className="mt-4 border-t border-wheat pt-4 space-y-4">
              <h3 className="text-sm font-semibold text-bark">
                ⚙ {t('googleSyncSettingsTitle')}
              </h3>

              {/* Auto-sync + interval */}
              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-wood">
                  <input
                    type="checkbox"
                    checked={googleSettings.autoSyncEnabled}
                    onChange={e => setGoogleSettings({ ...googleSettings, autoSyncEnabled: e.target.checked })}
                    className="w-4 h-4 accent-moss"
                  />
                  {t('googleSyncAutoSyncEnabled')}
                </label>
                {googleSettings.autoSyncEnabled && (
                  <label className="flex items-center gap-2 text-sm text-wood">
                    {t('googleSyncInterval')}:
                    <input
                      type="text"
                      inputMode="numeric"
                      min={1}
                      max={1440}
                      value={googleSettings.syncIntervalMinutes}
                      onChange={e => setGoogleSettings({ ...googleSettings, syncIntervalMinutes: Number(e.target.value.replace(',', '.')) })}
                      className="w-20 px-2 py-1 text-sm border border-wheat rounded bg-cream"
                    />
                    {t('minutes')}
                  </label>
                )}
              </div>

              {/* Sync direction */}
              <div>
                <label className="text-xs text-wood font-semibold block mb-1">
                  {t('googleSyncDirection')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {GOOGLE_SYNC_DIRECTIONS.map(dir => (
                    <button
                      key={dir}
                      onClick={() => setGoogleSettings({ ...googleSettings, syncDirection: dir })}
                      className={`text-xs px-3 py-1.5 rounded border transition ${
                        googleSettings.syncDirection === dir
                          ? 'bg-moss text-cream border-moss'
                          : 'bg-cream text-wood border-wheat hover:bg-wheat/30'
                      }`}
                    >
                      {dir === 'BIDIRECTIONAL' && '↕ '}
                      {dir === 'FROM_GOOGLE' && '← '}
                      {dir === 'TO_GOOGLE' && '→ '}
                      {t(`googleSyncDir_${dir}`) || dir}
                    </button>
                  ))}
                </div>
              </div>

              {/* Event types */}
              <div>
                <label className="text-xs text-wood font-semibold block mb-1">
                  {t('googleSyncEventTypes')}
                </label>
                <div className="flex flex-wrap gap-2">
                  {GOOGLE_EVENT_TYPES.map(type => {
                    const active = googleSettings.syncEventTypes.split(',').map(s => s.trim()).includes(type);
                    return (
                      <button
                        key={type}
                        onClick={() => toggleGoogleEventType(type)}
                        className={`text-xs px-3 py-1.5 rounded border transition ${
                          active
                            ? 'bg-moss text-cream border-moss'
                            : 'bg-cream text-wood border-wheat hover:bg-wheat/30'
                        }`}
                      >
                        {active ? '✓ ' : ''}{type}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Google calendar — multi-select */}
              <div>
                <label className="text-xs text-wood font-semibold block mb-1">
                  {t('googleSyncCalendarId')}
                </label>
                {googleCalendars.length > 0 ? (
                  <div className="border border-wheat rounded bg-cream max-w-md max-h-48 overflow-y-auto">
                    {googleCalendars.map(c => {
                      const selectedIds = (googleSettings.googleCalendarId || 'primary').split(',').map(s => s.trim()).filter(Boolean);
                      const checked = selectedIds.includes(c.id);
                      return (
                        <label key={c.id} className="flex items-center gap-2 px-3 py-2 hover:bg-bark/5 cursor-pointer border-b border-wheat/30 last:border-b-0">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={e => {
                              const next = e.target.checked
                                ? [...selectedIds, c.id]
                                : selectedIds.filter(id => id !== c.id);
                              setGoogleSettings({ ...googleSettings, googleCalendarId: next.length > 0 ? next.join(',') : 'primary' });
                            }}
                            className="w-4 h-4"
                          />
                          <span className="text-sm text-bark flex-1 truncate">{c.summary}</span>
                          <span className="text-xs text-wood">{c.id}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <input
                    type="text"
                    value={googleSettings.googleCalendarId}
                    onChange={e => setGoogleSettings({ ...googleSettings, googleCalendarId: e.target.value })}
                    placeholder="primary"
                    className="w-full max-w-md px-3 py-2 text-sm border border-wheat rounded bg-cream"
                  />
                )}
                <p className="text-xs text-wood mt-1">
                  {(googleSettings.googleCalendarId || 'primary').split(',').filter(Boolean).length} {t('calendarsSelected')}
                </p>
              </div>

              {/* Last sync */}
              {googleSettings.lastSyncAt && (
                <p className="text-xs text-wood">
                  {t('googleSyncLastSync')}: <span className="text-bark">{googleSettings.lastSyncAt}</span>
                </p>
              )}

              {/* Save */}
              <button
                onClick={handleSaveGoogleSettings}
                disabled={savingGoogleSettings}
                className="px-4 py-2 bg-moss text-cream rounded-rustic text-sm font-medium disabled:opacity-50"
              >
                {savingGoogleSettings ? '…' : (t('save'))}
              </button>
            </div>
          )}

          {/* Gmail sekce — Google OAuth už neobsahuje Gmail scope, mailbox se připojuje přes IMAP */}
          {googleStatus?.connected && (
            <div className="mt-4 border-t border-wheat pt-4">
              <h3 className="text-sm font-semibold text-bark mb-3">{t('gmailTitle')}</h3>
              {gmailAccount && (
                <div className="bg-ochre/20 border border-ochre/30 rounded-rustic p-2 mb-3">
                  <p className="text-ochre text-xs">
                    {t('gmailAccountInactive').replace('{email}', gmailAccount.email)}
                  </p>
                </div>
              )}
              <p className="text-xs text-wood">{t('gmailImapOnly')}</p>
            </div>
          )}
        </Card>
      )}

      {activeTab === 'license' && (
        <LicenseTab />
      )}

      {activeTab === 'settings' && (
        <Card>
          <h2 className="text-lg font-semibold text-bark mb-4">{t('settingsTitle')}</h2>
          <div className="grid gap-6">
            {/* Výběr jazyka */}
            <div>
              <label className="field-label block text-sm font-medium text-bark mb-2">{t('appLanguage')}</label>
              <select
                value={locale}
                onChange={(e) => changeLocale(e.target.value as Locale)}
                className="field-input font-sans cursor-pointer"
              >
                {getOrderedLanguages().map(([code, lang]) => (
                  <option key={code} value={code}>
                    {lang.flag} {lang.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Výběr tématu */}
            <div className="border-t border-wheat pt-4">
              <label className="field-label block text-sm font-medium text-bark mb-2">{t('appTheme')}</label>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setTheme('light')}
                  className={`flex-1 py-3 px-4 rounded-rustic border text-center transition flex items-center justify-center gap-2 font-medium ${
                    theme === 'light'
                      ? 'bg-moss text-cream border-moss font-semibold'
                      : 'bg-linen hover:bg-wheat/50 text-wood border-wheat'
                  }`}
                >
                  {t('lightTheme')}
                </button>
                <button
                  type="button"
                  onClick={() => setTheme('dark')}
                  className={`flex-1 py-3 px-4 rounded-rustic border text-center transition flex items-center justify-center gap-2 font-medium ${
                    theme === 'dark'
                      ? 'bg-moss text-cream border-moss font-semibold'
                      : 'bg-linen hover:bg-wheat/50 text-wood border-wheat'
                  }`}
                >
                  {t('darkTheme')}
                </button>
              </div>
            </div>

            {/* Velikost písma */}
            <div className="border-t border-wheat pt-4">
              <label className="field-label block text-sm font-medium text-bark mb-1">{t('fontSize')}</label>
              <p className="text-xs text-wood mb-3">{t('fontSizeHelp')}</p>
              <div className="flex gap-1.5 items-center flex-wrap">
                {(['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12', '13', '14', '15'] as FontSize[]).map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => setFontSize(size)}
                    title={`${FONT_SIZE_PX[size]}px`}
                    style={{ fontSize: `${FONT_SIZE_PX[size] - 4}px` }}
                    className={`w-8 h-8 rounded-full border-2 transition flex items-center justify-center shrink-0 font-bold ${
                      fontSize === size
                        ? 'bg-moss border-moss text-cream'
                        : 'bg-linen hover:bg-wheat/50 border-wheat text-wood'
                    }`}
                  >
                    A
                  </button>
                ))}
              </div>
            </div>

            {/* Země (národní prostředí) */}
            <div className="border-t border-wheat pt-4">
              <label className="field-label block text-sm font-medium text-bark mb-1">{t('profileCountry')}</label>
              <p className="text-xs text-wood mb-3">{t('profileCountryHint')}</p>
              <select
                value={profile?.country || ''}
                onChange={async (e) => {
                  const val = e.target.value || null;
                  try {
                    await updateUserCountry(val);
                    setProfile((p) => p ? { ...p, country: val } : p);
                    showToast(t('toastUpdated'), 'success');
                  } catch (err: any) {
                    showToast(err?.message || t('errSaveAddress'), 'error');
                  }
                }}
                className="field-input font-sans cursor-pointer"
              >
                <option value="">{t('profileCountryDefault')}</option>
                <option value="CZ">🇨🇿 Česko</option>
                <option value="SK">🇸🇰 Slovensko</option>
                <option value="DE">🇩🇪 Deutschland</option>
                <option value="RU">🇷🇺 Россия</option>
                <option value="UK">🇺🇦 Україна</option>
                <option value="US">🇺🇸 United States</option>
              </select>
            </div>

            {/* Automatické odhlášení (idle timeout) */}
            <div className="border-t border-wheat pt-4">
              <label className="field-label block text-sm font-medium text-bark mb-1">{t('idleTimeoutLabel')}</label>
              <p className="text-xs text-wood mb-3">
                {t('idleTimeoutRangeHint')
                  .replace('{min}', '15')
                  .replace('{max}', String(profile?.idleTimeoutMax ?? 480))
                  .replace('{def}', String(profile?.idleTimeoutDefault ?? 15))}
              </p>
              <input
                type="text"
                inputMode="numeric"
                min={15}
                max={profile?.idleTimeoutMax ?? 480}
                key={profile?.idleTimeoutMinutes ?? 'default'}
                defaultValue={profile?.idleTimeoutMinutes ?? ''}
                placeholder={String(profile?.idleTimeoutDefault ?? 15)}
                onBlur={async (e) => {
                  const raw = e.target.value.trim();
                  const minutes = raw === '' ? null : parseInt(raw.replace(',', '.'), 10);
                  const maxMin = profile?.idleTimeoutMax ?? 480;
                  if (raw !== '' && (isNaN(minutes!) || minutes! < 15 || minutes! > maxMin)) {
                    e.target.value = profile?.idleTimeoutMinutes != null ? String(profile.idleTimeoutMinutes) : '';
                    showToast(t('idleTimeoutInvalid').replace('{min}', '15').replace('{max}', String(maxMin)), 'error');
                    return;
                  }
                  if (minutes === (profile?.idleTimeoutMinutes ?? null)) return;
                  try {
                    await apiFetch('/api/auth/idle-timeout', {
                      method: 'POST',
                      body: JSON.stringify({ idleTimeoutMinutes: minutes }),
                    });
                    setProfile((p) => p ? { ...p, idleTimeoutMinutes: minutes } : p);
                    setIdleTimeoutMinutes(minutes ?? (profile?.idleTimeoutDefault ?? 15));
                    showToast(t('toastUpdated'), 'success');
                  } catch (err: any) {
                    showToast(err?.message || t('errSaveAddress'), 'error');
                  }
                }}
                className="field-input font-sans w-40"
              />
            </div>

            {/* Režim seznamů — stránkování / nekonečné rolování */}
            <div className="border-t border-wheat pt-4">
              <label className="field-label block text-sm font-medium text-bark mb-1">{t('listDisplayModeLabel')}</label>
              <p className="text-xs text-wood mb-3">{t('listDisplayModeHint')}</p>
              <select
                key={profile?.listDisplayMode ?? 'auto'}
                defaultValue={profile?.listDisplayMode || ''}
                onChange={async (e) => {
                  const mode = e.target.value || null;
                  try {
                    await apiFetch('/api/auth/list-display-mode', {
                      method: 'POST',
                      body: JSON.stringify({ mode }),
                    });
                    setProfile((p) => p ? { ...p, listDisplayMode: mode } : p);
                    setListDisplayMode(mode === 'scroll' || mode === 'paged' ? mode : 'auto');
                    showToast(t('toastUpdated'), 'success');
                  } catch (err: any) {
                    showToast(err?.message || t('errSaveAddress'), 'error');
                  }
                }}
                className="field-input font-sans cursor-pointer w-64"
              >
                <option value="">{t('listDisplayModeAuto')}</option>
                <option value="scroll">{t('listDisplayModeScroll')}</option>
                <option value="paged">{t('listDisplayModePaged')}</option>
              </select>
            </div>

            {/* Domovská stránka */}
            <div className="border-t border-wheat pt-4">
              <label className="field-label block text-sm font-medium text-bark mb-2">{t('homePageSection')}</label>
              <p className="text-xs text-wood mb-3">{t('widgetEditLayoutDesc')}</p>
              <button
                type="button"
                onClick={() => {
                  router.push('/?edit=true');
                  onNavigate?.('/?edit=true');
                }}
                className="btn-secondary"
              >
                ✎ {t('widgetEditLayout')}
              </button>
            </div>

            {/* Pořadí mého menu */}
            <div className="border-t border-wheat pt-4">
              <label className="field-label block text-sm font-medium text-bark mb-2">{t('menuOrderTitle')}</label>
              <p className="text-xs text-wood mb-3">{t('menuOrderDesc')}</p>
              <a
                href="/menu-order"
                className="btn-secondary inline-block"
              >
                ☰ {t('menuOrderEditBtn')}
              </a>
            </div>
          </div>
        </Card>
      )}

      {/* MODAL PRO PŘIDÁNÍ/ÚPRAVU ADRESY */}
      {(showAddressModal || addressModalExiting) && (
        <div
          className={`fixed inset-0 z-50 flex items-center justify-center bg-bark/40 backdrop-blur-sm overflow-y-auto ${showAddressModal ? 'animate-backdropIn' : 'animate-backdropOut'}`}
          onClick={(e) => { if (e.target === e.currentTarget) setShowAddressModal(false); }}
        >
          <div role="dialog" aria-modal="true" className={`bg-linen border border-wheat rounded-rustic shadow-xl p-6 w-full max-w-lg mx-4 my-8 overflow-y-auto max-h-[90vh] ${showAddressModal ? 'animate-modalIn' : 'animate-modalOut'}`}>
            <h3 className="text-lg font-semibold text-bark mb-4">
              🏢 {editingAddress ? t('editAddress') : t('addNewAddress')}
            </h3>

            <form onSubmit={handleSaveAddress} className={`space-y-4 ${addressSaving ? 'cursor-wait' : ''}`} noValidate>
              {/* Název adresy */}
              <div>
                <label className="field-label" htmlFor="addressName">{t('addressNameLabel')}</label>
                <input
                  id="addressName"
                  type="text"
                  className={`field-input ${showNameError ? 'field-input-invalid' : ''}`}
                  value={addressForm.addressName}
                  onChange={(e) => setAddressForm((s) => ({ ...s, addressName: e.target.value }))}
                  placeholder={t('addressNamePlaceholder')}
                />
                {showNameError && (
                  <p className="field-error-text">{t('addressNameRequired')}</p>
                )}
              </div>

              {/* Typ adresy */}
              <div>
                <label className="field-label">{t('addressTypeLabel')}</label>
                <div className="flex gap-2 mt-1">
                  <button
                    type="button"
                    onClick={() => setAddressForm((s) => ({ ...s, addressType: 'BILLING' }))}
                    className={`flex-1 py-2 px-3 rounded-rustic text-sm font-semibold border transition ${
                      addressForm.addressType === 'BILLING'
                        ? 'bg-rust text-linen border-rust'
                        : 'bg-linen text-wood border-wheat hover:border-wood'
                    }`}
                  >
                    🧾 {t('billingAddressType')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddressForm((s) => ({ ...s, addressType: 'DELIVERY' }))}
                    className={`flex-1 py-2 px-3 rounded-rustic text-sm font-semibold border transition ${
                      addressForm.addressType === 'DELIVERY'
                        ? 'bg-moss text-linen border-moss'
                        : 'bg-linen text-wood border-wheat hover:border-wood'
                    }`}
                  >
                    🚚 {t('deliveryAddressType')}
                  </button>
                </div>
              </div>

              {/* Název firmy */}
              <div>
                <label className="field-label" htmlFor="addrCompanyName">{t('companyName')}</label>
                <input
                  id="addrCompanyName"
                  type="text"
                  className="field-input"
                  value={addressForm.companyName}
                  onChange={(e) => setAddressForm((s) => ({ ...s, companyName: e.target.value }))}
                />
              </div>

              {/* Jméno a Příjmení odběratele */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="field-label" htmlFor="addrFirstName">{t('firstName')}</label>
                  <input
                    id="addrFirstName"
                    type="text"
                    className="field-input"
                    value={addressForm.firstName}
                    onChange={(e) => setAddressForm((s) => ({ ...s, firstName: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="addrLastName">{t('lastName')}</label>
                  <input
                    id="addrLastName"
                    type="text"
                    className="field-input"
                    value={addressForm.lastName}
                    onChange={(e) => setAddressForm((s) => ({ ...s, lastName: e.target.value }))}
                  />
                </div>
              </div>

              {/* Ulice */}
              <div>
                <label className="field-label" htmlFor="addrStreet">{t('streetLabel')}</label>
                <input
                  id="addrStreet"
                  type="text"
                  className="field-input"
                  value={addressForm.street}
                  onChange={(e) => setAddressForm((s) => ({ ...s, street: e.target.value }))}
                />
              </div>

              {/* Město a PSČ */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="field-label" htmlFor="addrCity">{t('cityLabel')}</label>
                  <input
                    id="addrCity"
                    type="text"
                    className="field-input"
                    value={addressForm.city}
                    onChange={(e) => setAddressForm((s) => ({ ...s, city: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="field-label" htmlFor="addrZip">{t('zipLabel')}</label>
                  <input
                    id="addrZip"
                    type="text"
                    className={`field-input ${showZipError ? 'field-input-invalid' : ''}`}
                    value={addressForm.zip}
                    onChange={(e) => {
                      setAddressForm((s) => ({ ...s, zip: e.target.value }));
                      setAddressZipTouched(true);
                    }}
                    onBlur={() => setAddressZipTouched(true)}
                  />
                  {showZipError && (
                    <p className="field-error-text">{t('invalidZip')}</p>
                  )}
                </div>
              </div>

              {/* Země */}
              <div>
                <label className="field-label" htmlFor="addrCountry">{t('countryLabel')}</label>
                <input
                  id="addrCountry"
                  type="text"
                  className="field-input"
                  value={addressForm.country}
                  onChange={(e) => setAddressForm((s) => ({ ...s, country: e.target.value }))}
                />
              </div>

              {/* IČ a DIČ */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="field-label" htmlFor="addrIc">{t('icLabel')}</label>
                  <div className="flex gap-2">
                    <input
                      id="addrIc"
                      type="text"
                      className={`field-input flex-1 ${showIcError ? 'field-input-invalid' : ''} ${aresLoading ? 'cursor-wait' : ''}`}
                      value={addressForm.ic}
                      onChange={(e) => {
                        setAddressForm((s) => ({ ...s, ic: e.target.value }));
                        setAddressIcTouched(true);
                      }}
                      onBlur={() => setAddressIcTouched(true)}
                    />
                    <button
                      type="button"
                      onClick={handleAresLookup}
                      disabled={aresLoading}
                      className="btn-secondary px-3 font-semibold whitespace-nowrap self-stretch flex items-center justify-center gap-1"
                    >
                      {aresLoading && (
                        <div className="w-3.5 h-3.5 border-2 border-wood/20 border-t-rust rounded-full animate-spin" />
                      )}
                      🔍 ARES
                    </button>
                  </div>
                  {showIcError && (
                    <p className="field-error-text">{t('invalidIc')}</p>
                  )}
                </div>
                <div>
                  <label className="field-label" htmlFor="addrDic">{t('dicLabel')}</label>
                  <div className="flex gap-2">
                    <input
                      id="addrDic"
                      type="text"
                      className={`field-input flex-1 ${showDicError ? 'field-input-invalid' : ''} ${viesLoading ? 'cursor-wait' : ''}`}
                      value={addressForm.dic}
                      onChange={(e) => {
                        setAddressForm((s) => ({ ...s, dic: e.target.value }));
                        setAddressDicTouched(true);
                      }}
                      onBlur={() => setAddressDicTouched(true)}
                    />
                    <button
                      type="button"
                      onClick={handleViesLookup}
                      disabled={viesLoading}
                      className="btn-secondary px-3 font-semibold whitespace-nowrap self-stretch flex items-center justify-center gap-1"
                    >
                      {viesLoading && (
                        <div className="w-3.5 h-3.5 border-2 border-wood/20 border-t-rust rounded-full animate-spin" />
                      )}
                      🔍 VIES
                    </button>
                  </div>
                  {showDicError && (
                    <p className="field-error-text">{t('invalidDic')}</p>
                  )}
                </div>
              </div>

              {/* Je výchozí */}
              <div className="flex items-center gap-2 pt-2">
                <input
                  id="addrDefault"
                  type="checkbox"
                  checked={addressForm.isDefault}
                  onChange={(e) => setAddressForm((s) => ({ ...s, isDefault: e.target.checked }))}
                  className="rounded border-wheat text-moss focus:ring-moss h-4 w-4 bg-linen"
                  disabled={editingAddress?.isDefault} // nelze zrušit výchozí příznak, pokud je jediný/aktivní
                />
                <label className="text-xs text-bark font-medium cursor-pointer" htmlFor="addrDefault">
                  {t('setDefaultBillingAddress')}
                </label>
              </div>

              {/* Akce modálu */}
              <div className="flex gap-3 justify-end pt-4 border-t border-wheat/40">
                <button
                  type="button"
                  onClick={() => setShowAddressModal(false)}
                  className="btn-secondary"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  disabled={addressSaving}
                  className="btn-primary"
                >
                  {addressSaving ? '…' : t('saveBtn')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {message && <Alert type="success">{message}</Alert>}
      {error && <Alert type="error">{error}</Alert>}

      <ConfirmModal
        open={addressToDelete !== null}
        title={t('deleteAddressConfirm')}
        message={t('deleteAddressMessage')}
        confirmLabel={t('addressDelete')}
        cancelLabel={t('cancel')}
        danger={true}
        onConfirm={async () => {
          if (addressToDelete !== null) {
            await handleDeleteAddress(addressToDelete);
            setAddressToDelete(null);
          }
        }}
        onCancel={() => setAddressToDelete(null)}
      />
        </div>
      </div>
    </div>
  );
}
